/**
 * Tracks the offset between the server clock and the client clock.
 *
 * The offset is computed from the `Date` response header of API calls:
 *   offsetMs = serverTime - clientTime
 *
 * A positive offset means the client clock is behind the server;
 * a negative offset means the client clock is ahead.
 *
 * `getServerNow()` returns a `Date` adjusted by this offset, giving
 * a best-effort approximation of the current server time regardless
 * of how skewed the client's system clock is.
 */
let offsetMs = 0;

/**
 * Call this with the value of a `Date` response header to recalibrate
 * the clock offset.  Invalid or missing values are silently ignored so
 * that the caller never needs to guard against bad input.
 */
export function updateClockOffset(serverDateHeader: string): void {
  const serverTime = new Date(serverDateHeader).getTime();

  if (Number.isNaN(serverTime)) {
    return;
  }

  offsetMs = serverTime - Date.now();
}

/**
 * Returns a `Date` representing "now" according to the server clock.
 * If no offset has been computed yet (e.g. before the first API call),
 * this falls back to the raw client clock (`offsetMs === 0`).
 */
export function getServerNow(): Date {
  return new Date(Date.now() + offsetMs);
}

/**
 * Returns the currently-tracked clock offset in milliseconds
 * (`serverTime - clientTime`). Useful for diagnostics when the
 * backend rejects a token the client considers fresh — large offset
 * suggests the device clock drift is the cause.
 */
export function getClockOffsetMs(): number {
  return offsetMs;
}
